import * as React from 'react';
import { MULTI_LOCATION_NOTE, emptyStateCtaFor, emptyStateFor, emptyStateStyleFor, filtersFor } from '../config';
import { activitiesApi } from '../services/activitiesApi';
import { getUnreadCount, publishUnreadCount, resumeUnreadPolling } from '../services/unreadCountStore';
import type { ActivityItem as ActivityItemType, Audience, FeedFilter } from '../types';
import { ActivityFeed } from './ActivityFeed';
import { EmptyState } from './EmptyState';
import { FeedFilters } from './FeedFilters';

// The Rails view renders the six shortcut cards above this component; it only
// owns the activity feed panel.
export type DashboardPageProps = {
  audience: Audience;
  multi_location?: boolean;
  // Where the empty state's one button goes (new job for practices, job
  // search for professionals). Falls back to the usual paths.
  empty_cta_href?: string | null;
  // Where the Temp chip's empty state button goes (schedule a temp for
  // practices, availability for professionals).
  temp_cta_href?: string | null;
};

export function DashboardPage({
  audience,
  multi_location = false,
  empty_cta_href = null,
  temp_cta_href = null
}: DashboardPageProps) {
  const filters = React.useMemo(() => filtersFor(audience), [audience]);

  const [activeFilter, setActiveFilter] = React.useState<FeedFilter>(filters[0]);
  const [unread, setUnread] = React.useState(0);
  const [items, setItems] = React.useState<ActivityItemType[]>([]);
  const [nextCursor, setNextCursor] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const loadingMoreRef = React.useRef(false);
  const feedGenerationRef = React.useRef(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const count = await activitiesApi.unreadCount();
        if (cancelled) return;
        setUnread(count.unread);
        // Keep the nav bells in step with the count this page just loaded.
        publishUnreadCount(count.unread);
        resumeUnreadPolling();
      } catch {
        // The feed still loads; the mark-all button just stays hidden.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    // A new filter starts a new generation. Any first page or load-more page
    // still in flight for the old filter is dropped when it lands.
    feedGenerationRef.current += 1;
    const generation = feedGenerationRef.current;
    const isCurrent = () => feedGenerationRef.current === generation;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setLoading(true);
    setItems([]);
    setNextCursor(null);
    (async () => {
      try {
        const page = await activitiesApi.list({ group: activeFilter.group });
        if (!isCurrent()) return;
        setItems(page.activities);
        setNextCursor(page.next_cursor);
        setError(null);
        resumeUnreadPolling();
      } catch {
        if (isCurrent()) {
          setItems([]);
          setError('Could not load your feed.');
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    })();
    return () => {
      // Unmount also invalidates the request.
      feedGenerationRef.current += 1;
    };
  }, [activeFilter.group, activeFilter.id]);

  const loadMore = React.useCallback(async () => {
    if (!nextCursor || loadingMoreRef.current) return;
    const generation = feedGenerationRef.current;
    const isCurrent = () => feedGenerationRef.current === generation;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = await activitiesApi.list({ cursor: nextCursor, group: activeFilter.group });
      if (!isCurrent()) return;
      setItems((prev) => [...prev, ...page.activities]);
      setNextCursor(page.next_cursor);
    } catch {
      // Keep the rows we already have.
    } finally {
      if (isCurrent()) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [activeFilter.group, nextCursor]);

  const openItem = async (item: ActivityItemType) => {
    try {
      await activitiesApi.markRead(item.id);
      setItems((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, read_at: row.read_at || new Date().toISOString() } : row))
      );
      if (!item.read_at) {
        setUnread((count) => Math.max(0, count - 1));
        publishUnreadCount(Math.max(0, getUnreadCount() - 1));
      }
    } catch {
      // Follow the link even if mark-read fails.
    }
    const url = item.action?.web_url;
    if (url) window.location.assign(url);
  };

  const markAllRead = async () => {
    try {
      await activitiesApi.markAllRead();
      const stamped = new Date().toISOString();
      setItems((prev) => prev.map((item) => ({ ...item, read_at: item.read_at || stamped })));
      setUnread(0);
      publishUnreadCount(0);
    } catch {
      setError('Could not mark activities as read.');
    }
  };

  const empty = !loading && items.length === 0;

  return (
    <section className="activity-dashboard dashboard-panel" aria-labelledby="activity-dashboard-heading">
      <div className="activity-dashboard__feed-header">
        <div className="activity-dashboard__heading">
          <h2 id="activity-dashboard-heading" className="activity-dashboard__title">
            Recent activity
          </h2>
          {unread > 0 ? (
            <button type="button" className="btn btn-sm btn-link activity-dashboard__mark-all" onClick={markAllRead}>
              Mark all as read
            </button>
          ) : null}
        </div>
        <FeedFilters filters={filters} activeId={activeFilter.id} onSelect={setActiveFilter} />
      </div>
      {audience === 'practice' && multi_location ? (
        <p className="activity-dashboard__note">{MULTI_LOCATION_NOTE}</p>
      ) : null}
      {error ? (
        <div className="alert alert-warning mb-3" role="alert">
          {error}
        </div>
      ) : null}
      {empty ? (
        <EmptyState
          copy={emptyStateFor(audience, activeFilter.id)}
          cta={emptyStateCtaFor(audience, empty_cta_href, activeFilter.id, temp_cta_href)}
          badge={emptyStateStyleFor(activeFilter.id)}
        />
      ) : (
        <ActivityFeed
          items={items}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={nextCursor != null}
          onOpen={openItem}
          onLoadMore={loadMore}
          audience={audience}
        />
      )}
    </section>
  );
}
